import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Tag } from 'antd';
import {
  orderTypeColors,
  orderTypeLabels,
} from 'apps/investment/constants/fundOrder';
import {
  userProgramOrderStatus,
  userProgramOrderStatusColors,
} from 'apps/investment/constants/programOrder';
import {
  orderSideColors,
  orderSideLabels,
  orderStatusColors,
  orderTypeColors as stockOrderTypeColors,
} from 'apps/investment/constants/stockOrder';
import { DataTableColumnHeader } from 'common/data-table-column-header';
import { Button } from 'common/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'common/ui/tooltip';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  currencyCell,
  dateCell,
  dateTimeCell,
  linkCell,
  numberCell,
  upperCaseCell,
  userCell,
} from 'utils/cell-formatter';
import { formatDateTime } from 'utils/formatDate';

export const columnsGenerator = params => [
  {
    accessorKey: 'id',
    header: '',
    cell: ({ getValue }) => linkCell({ getValue, path: '/investment/orders' }),
  },

  {
    accessorKey: 'order_date',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Order Date" />
    ),
    cell: dateTimeCell,
  },

  {
    header: 'Type',
    accessorKey: 'order_type',
    cell: ({ getValue }) => {
      const value = getValue();
      return <Tag color={orderTypeColors[value]}>{orderTypeLabels[value]}</Tag>;
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ getValue, row }) => {
      const value = getValue();
      const status_label = row.original.status_label;
      return (
        <Tag color={userProgramOrderStatusColors[value]}>{status_label}</Tag>
      );
    },
  },
  {
    header: 'Amount',
    accessorKey: 'amount',
    cell: currencyCell,
  },
  {
    header: 'Price',
    accessorKey: 'price',
    cell: currencyCell,
  },
  {
    header: 'Quantity',
    accessorKey: 'quantity',
    cell: numberCell,
  },
  {
    header: 'Matched Amount',
    accessorKey: 'matched_amount',
    cell: currencyCell,
  },
  {
    header: 'Matched Price',
    accessorKey: 'matched_price',
    cell: currencyCell,
  },
  {
    header: 'Matched Quantity',
    accessorKey: 'matched_quantity',
    cell: numberCell,
  },
  {
    header: 'Product',
    accessorKey: 'product_code',
  },
  {
    header: 'User',
    accessorKey: 'user',
    cell: userCell,
  },

  {
    accessorKey: 'source',
    header: 'Product Source',
    cell: ({ row, getValue }) => {
      const source = getValue();
      const id = row.original.id;

      return (
        <div>
          {source === 1001 && (
            <Link to={`/investment/fund-orders?order_id=${id}`}>
              <div className="flex items-center">
                FO
                <ArrowRight size={14} />
              </div>
            </Link>
          )}
          {source === 1002 && (
            <Link to={`/investment/stock-orders?order_id=${id}`}>
              <div className="flex items-center">
                SO
                <ArrowRight size={14} />
              </div>
            </Link>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'created_at',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: dateTimeCell,
  },
  {
    accessorKey: 'updated_at',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Updated" />
    ),
    cell: dateTimeCell,
  },
];

export const rebundleColumns = [
  {
    title: 'ID',
    dataIndex: 'code',
    render: (value, record) => (
      <Link to={`/investment/orders/${record.id}`}>{value}</Link>
    ),
  },
  {
    title: 'User',
    dataIndex: 'user',
    render: value => (value && value.email ? value.email : '-'),
  },
  {
    title: 'Order Type',
    dataIndex: 'order_type',
    align: 'center',
    render: (text, record) => {
      let orderType = record.order_type;

      return (
        <Tag color={orderTypeColors[orderType]}>
          {orderTypeLabels[orderType]}
        </Tag>
      );
    },
  },
  {
    title: 'Amount',
    dataIndex: 'amount',
    type: 'formatNumber',
  },
  {
    title: 'Product Code',
    dataIndex: 'product_code',
    align: 'center',
  },

  {
    title: 'Created',
    dataIndex: 'created_at',
    render: value => formatDateTime(value),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    align: 'center',
    render: status => {
      return (
        <Tag color={userProgramOrderStatusColors[status]}>
          {userProgramOrderStatus[status]}
        </Tag>
      );
    },
  },
  {
    title: 'Fund Order',
    dataIndex: 'order',
    render: order => {
      if (!order) {
        return '-';
      }

      return (
        <Link to={`/investment/fund-orders/${order.id}`}>{order.code}</Link>
      );
    },
  },
];

export const stockOrdersColumnsGenerator = () => [
  {
    accessorKey: 'id',
    header: '',
    cell: ({ getValue }) =>
      linkCell({ getValue, path: '/investment/stock-orders' }),
  },
  {
    accessorKey: 'trade_date',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Order Date" />
    ),
    cell: dateCell,
  },
  {
    accessorKey: 'order_side',
    header: 'Order Side',
    cell: ({ getValue }) => {
      const value = getValue();
      return <Tag color={orderSideColors[value]}>{orderSideLabels[value]}</Tag>;
    },
  },
  {
    accessorKey: 'order_type',
    header: 'Order Type',
    cell: ({ getValue }) => {
      const value = getValue();
      return (
        <Tag color={stockOrderTypeColors[value]}>{value?.toUpperCase()}</Tag>
      );
    },
  },
  {
    accessorKey: 'symbol',
    header: 'Symbol',
  },
  {
    header: 'Status',
    accessorKey: 'status',
    cell: ({ row, getValue }) => {
      const status = getValue();
      const label = row.original.status_label;
      const security_order_message = row.original.security_order_message;

      if (security_order_message) {
        return (
          <div>
            {security_order_message && (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild className="p-0">
                    <Button variant="none">
                      <Tag
                        color={orderStatusColors[status]}
                        icon={<ExclamationCircleOutlined />}
                      >
                        {label}
                      </Tag>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{security_order_message}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );
      }
      return <Tag color={orderStatusColors[status]}>{label}</Tag>;
    },
  },

  {
    accessorKey: 'price',
    header: 'Price',
    cell: numberCell,
  },
  {
    accessorKey: 'volume',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Volume" />
    ),
    cell: numberCell,
  },
  {
    accessorKey: 'value',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Value" />
    ),
    cell: numberCell,
  },
  {
    accessorKey: 'matched_price',
    header: 'Matched Price',
    cell: numberCell,
  },

  {
    accessorKey: 'matched_volume',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Matched Volume" />
    ),
    cell: numberCell,
  },

  {
    accessorKey: 'matched_value',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Matched Value" />
    ),
    cell: numberCell,
  },

  {
    accessorKey: 'created_at',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: dateTimeCell,
  },
  {
    accessorKey: 'updated_at',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Updated" />
    ),
    cell: dateTimeCell,
  },
];

export const fundOrdersColumnsGenerator = () => [
  {
    accessorKey: 'id',
    header: '',
    cell: ({ getValue }) =>
      linkCell({ getValue, path: '/investment/fund-orders' }),
  },

  {
    accessorKey: 'order_date',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Order Date" />
    ),
    cell: dateTimeCell,
  },
  {
    header: 'Order Type',
    accessorKey: 'order_side',
    cell: ({ getValue }) => {
      const value = getValue();
      return <Tag color={orderTypeColors[value]}>{orderTypeLabels[value]}</Tag>;
    },
  },

  {
    header: 'Status',
    accessorKey: 'status',
    cell: ({ row, getValue }) => {
      const status = getValue();
      const label = row.original.status_label;
      return <Tag color={orderStatusColors[status]}>{label}</Tag>;
    },
  },
  {
    header: 'Amount',
    accessorKey: 'amount',
    cell: numberCell,
  },
  {
    header: 'Quantity',
    accessorKey: 'quantity',
    cell: numberCell,
  },

  {
    header: 'Fund Order ID',
    accessorKey: 'fund_order_id',
  },
  {
    header: 'Product Code',
    accessorKey: 'fund_code',
  },
  {
    header: 'Trading Account Manager',
    accessorKey: 'trading_account_manager',
    cell: upperCaseCell,
  },

  {
    header: 'Created',
    accessorKey: 'created_at',
    cell: dateTimeCell,
  },

  {
    accessorKey: 'updated_at',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Updated" />
    ),
    cell: dateTimeCell,
  },
];
